import { ChangeDetectionStrategy, Component } from '@angular/core';

import { TabBarComponent } from './tab-bar.component';
import { money } from './money';
import { currentLocale } from './locale';

/**
 * Plan: "How is the plan holding up?"
 *
 * Structure taken from a 0.3.0 screenshot:
 *
 * - The kicker row is three things: the period stepper, an add button, and the
 *   budget pill.
 * - Under the subline sits the assigned bar. It is one segment per category,
 *   in that category's hue, over a track that stands for everything that came
 *   in. What is assigned and what is not are labelled beneath it.
 * - The list has a two column head, CATEGORY and SPENT / ASSIGNED.
 * - Each row states spent over assigned, then a bar, then the state in words
 *   under the bar. Going over is a sentence, not a colour: there is no red
 *   anywhere in this system.
 *
 * The figures follow the rest of the page. Assigned 3,400 less spent 2,248 is
 * 1,152, which is exactly the numerator the Today screen divides by 18 days to
 * reach 64 a day.
 */
@Component({
  selector: 'app-screen-plan',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TabBarComponent],
  template: `
    <div class="nq-screen">
      <div class="band">
        <div class="kicker-row">
          <span class="pill">
            <span class="pill__chev">‹</span> {{ t.period }}
            <span class="pill__chev">›</span>
          </span>
          <span class="plan-add" aria-hidden="true">+</span>
          <span class="pill">
            {{ t.budgetPill }} <span class="pill__caret">⌄</span>
          </span>
        </div>

        <div class="hero">
          <p class="hero__label">{{ t.leftLabel }}</p>
          <p class="hero__figure">{{ left }}</p>
          <p class="hero__subline">{{ t.subline }}</p>

          <div class="assigned">
            <span class="assigned__track">
              @for (line of lines; track line.name) {
              <span
                class="assigned__seg"
                [style.width.%]="line.share"
                [style.background]="line.hue"
              ></span>
              }
            </span>
            <div class="assigned__labels">
              <span>{{ assigned }} {{ t.assigned }}</span>
              <span>{{ unassigned }} {{ t.unassigned }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="ground">
        <div class="section-row">
          <span class="section">{{ t.category }}</span>
          <span class="section-value">{{ t.spentAssigned }}</span>
        </div>

        @for (line of lines; track line.name) {
        <div class="cat">
          <div class="cat__head">
            <span class="mark" [style.background]="line.hue">{{
              line.initial
            }}</span>
            <span class="row__title">{{ line.name }}</span>
            <span class="cat__amounts">
              <span class="cat__spent">{{ line.spent }}</span>
              <span class="cat__assigned"> / {{ line.assigned }}</span>
            </span>
          </div>
          <span class="bar">
            <span
              class="bar__fill"
              [style.width.%]="line.pct"
              [style.background]="line.hue"
            ></span>
          </span>
          <span class="cat__state">{{ line.state }}</span>
        </div>
        }

        <p class="cat__note">{{ t.note }}</p>
        <span class="link">{{ t.assignLink }} →</span>
      </div>

      <app-tab-bar active="plan" />
    </div>
  `,
  styles: [
    `
      .nq-screen .pill__chev {
        font-size: 15px;
        line-height: 1;
        color: var(--color-accent-200);
      }

      .nq-screen .pill {
        gap: 11.2px;
        font-size: 13px;
      }

      .nq-screen .plan-add {
        font-size: 20px;
        line-height: 1;
        color: var(--color-accent-200);
      }

      .nq-screen .assigned {
        margin-top: 22.4px;
      }

      .nq-screen .assigned__track {
        display: flex;
        gap: 2.8px;
        height: 6px;
        border-radius: 2px;
        overflow: hidden;
        background: color-mix(
          in srgb,
          var(--color-neutral-100) 18%,
          transparent
        );
      }

      .nq-screen .assigned__seg {
        display: block;
        height: 100%;
        border-radius: 2px;
      }

      .nq-screen .assigned__labels {
        display: flex;
        justify-content: space-between;
        gap: 11.2px;
        margin-top: 8.4px;
        font-size: 12px;
        color: var(--color-accent-200);
      }

      .nq-screen .cat {
        padding: 8.4px 0 11.2px;
      }

      .nq-screen .cat__head {
        display: flex;
        align-items: center;
        gap: 11.2px;
      }

      .nq-screen .cat__head .row__title {
        flex: 1;
      }

      .nq-screen .cat__amounts {
        white-space: nowrap;
      }

      .nq-screen .cat__spent {
        font-size: 15px;
        font-weight: 400;
        color: var(--color-accent);
      }

      .nq-screen .cat__assigned {
        font-size: 12px;
        font-weight: 400;
        color: var(--color-neutral-500);
      }

      .nq-screen .bar {
        display: block;
        margin-top: 8.4px;
        height: 3px;
        border-radius: 2px;
        background: var(--color-line);
        overflow: hidden;
      }

      .nq-screen .bar__fill {
        display: block;
        height: 100%;
        border-radius: 2px;
      }

      .nq-screen .cat__state {
        display: block;
        margin-top: 8.4px;
        font-size: 12px;
        font-weight: 400;
        color: var(--color-neutral-500);
      }

      .nq-screen .cat__note {
        margin-top: 16.8px;
        font-size: 13px;
        font-weight: 400;
        color: var(--color-neutral-500);
      }
    `,
  ],
})
export class ScreenPlanComponent {
  private readonly locale = currentLocale();

  protected readonly t = {
    period: $localize`:@@framePeriod:August 2026`,
    budgetPill: $localize`:@@frameBudgetPill:My Budget`,
    leftLabel: $localize`:@@framePlanLabel:Left to spend this period`,
    subline: $localize`:@@framePlanSubline:18 days left. You have spent 66% of the plan.`,
    assigned: $localize`:@@frameAssigned:assigned`,
    unassigned: $localize`:@@frameUnassigned:unassigned`,
    category: $localize`:@@frameCategory:Category`,
    spentAssigned: $localize`:@@frameSpentAssigned:Spent / assigned`,
    note: $localize`:@@frameCatNote:6 categories have nothing assigned yet.`,
    assignLink: $localize`:@@frameAssignLink:Assign or remove them`,
  };

  /** Assigned 3,400 less spent 2,248. The same 1,152 Today divides by 18. */
  protected readonly left = money(1152, this.locale);

  /** Of 4,000 that came in, 3,400 has a job and 600 does not. */
  protected readonly assigned = money(3400, this.locale);
  protected readonly unassigned = money(600, this.locale);

  /**
   * `share` is this category's slice of the 4,000 that came in, which is what
   * the band's bar draws. `pct` is spent over assigned, which is what the
   * row's own bar draws. They are different numbers on purpose.
   */
  protected readonly lines = [
    {
      name: $localize`:@@frameCatFood:Food`,
      spent: money(515, this.locale),
      assigned: money(900, this.locale),
      state: $localize`:@@frameStateLeft:Left` + ' ' + money(385, this.locale),
      pct: 57,
      share: 22.5,
      hue: '#ed8a5c',
      initial: 'F',
    },
    {
      name: $localize`:@@frameCatHousing:Housing`,
      spent: money(1500, this.locale),
      assigned: money(1500, this.locale),
      state: $localize`:@@frameStatePaid:Paid in full`,
      pct: 100,
      share: 37.5,
      hue: '#6e9bf0',
      initial: 'H',
    },
    {
      name: $localize`:@@frameCatTransport:Transport`,
      spent: money(233, this.locale),
      assigned: money(200, this.locale),
      state:
        $localize`:@@frameStateOver:Over by` + ' ' + money(33, this.locale),
      pct: 100,
      share: 5,
      hue: '#a6c25e',
      initial: 'T',
    },
    {
      name: $localize`:@@frameCatSavings:Savings`,
      spent: money(0, this.locale),
      assigned: money(800, this.locale),
      state: $localize`:@@frameStateUntouched:Untouched`,
      pct: 0,
      share: 20,
      hue: '#4fb8a6',
      initial: 'S',
    },
  ];
}
